import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiTrendingUp, 
  FiTrendingDown, 
  FiCalendar, 
  FiClock, 
  FiTarget, 
  FiAward,
  FiBookOpen,
  FiFilter,
  FiDownload,
  FiChevronRight
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { analyticsAPI, subjectsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const Progress = () => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const { showError } = useNotification();

  useEffect(() => {
    let cancelled = false;
    subjectsAPI.getSubjectNames()
      .then((res) => {
        const data = res?.data?.data ?? res?.data ?? {};
        const names = Array.isArray(data.names) ? data.names : [];
        if (!cancelled) setSubjects(names);
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchProgressData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getProgressData({
        period: selectedPeriod,
        subject: selectedSubject
      });
      const data = response?.data?.data ?? response?.data;
      setProgressData(data || null);
    } catch (error) {
      showError('Failed to load progress data');
      setProgressData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedSubject, showError]);

  useEffect(() => {
    fetchProgressData();
  }, [fetchProgressData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getProgressLevel = (progress) => {
    if (progress >= 90) return { level: 'Excellent', variant: 'success' };
    if (progress >= 75) return { level: 'Good', variant: 'primary' };
    if (progress >= 50) return { level: 'Average', variant: 'info' };
    if (progress >= 25) return { level: 'Below Average', variant: 'warning' };
    return { level: 'Needs Improvement', variant: 'danger' };
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return <FiTrendingUp className="trend-up" />;
    if (trend < 0) return <FiTrendingDown className="trend-down" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const badgeClass = (v) => ({ success: 'bg-emerald-100 text-emerald-800', primary: 'bg-slate-900 text-white', info: 'bg-sky-100 text-sky-800', warning: 'bg-amber-100 text-amber-800', danger: 'bg-red-100 text-red-800' }[v] || 'bg-slate-100 text-slate-600');
  const selectClass = 'px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30';

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Learning Progress</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track your learning journey and achievements.</p>
          </div>
          <button type="button" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors">
            <FiDownload className="h-4 w-4" /> Export
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <FiFilter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">View</span>
            </div>
            <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} className={selectClass}>
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="quarter">This quarter</option>
              <option value="year">This year</option>
            </select>
            <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className={selectClass}>
              <option value="all">All subjects</option>
              {subjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiTarget className="h-5 w-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">Overall Progress</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full flex items-center justify-center shrink-0" style={{ background: `conic-gradient(#10b981 ${(progressData?.overallProgress || 0) * 3.6}deg, #e2e8f0 0deg)` }}>
                <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center text-sm font-semibold text-slate-900">{formatPercentage(progressData?.overallProgress)}</div>
              </div>
              <div className="flex flex-col gap-1">
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium w-fit ${badgeClass(getProgressLevel(progressData?.overallProgress).variant)}`}>{getProgressLevel(progressData?.overallProgress).level}</span>
                <span className="text-xs text-slate-500 flex items-center gap-1">{getTrendIcon(progressData?.progressTrend)}{formatPercentage(Math.abs(progressData?.progressTrend || 0))} vs last period</span>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiBookOpen className="h-5 w-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">Learning Stats</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: progressData?.totalLessons, l: 'Lessons' }, { v: progressData?.totalTests, l: 'Tests' }, { v: formatTime(progressData?.totalTime), l: 'Time' }, { v: progressData?.streakDays, l: 'Streak' }].map(({ v, l }) => (
                <div key={l}>
                  <div className="text-lg font-semibold text-slate-900">{v ?? '—'}</div>
                  <div className="text-xs text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FiAward className="h-5 w-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">Achievements</span>
            </div>
            <div className="space-y-2 mb-3">
              {(progressData?.recentAchievements || []).slice(0, 3).map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <FiAward className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-slate-700">{a.title}</span>
                  <span className="text-xs text-slate-400 ml-auto">{formatDate(a.date)}</span>
                </div>
              ))}
            </div>
            <button type="button" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              View all <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {progressData?.subjectProgress && progressData.subjectProgress.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
            <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Progress by subject</h3>
            <div className="space-y-4">
              {progressData.subjectProgress.map((subject, index) => {
                const progressLevel = getProgressLevel(subject.progress);
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-slate-900">{subject.name}</h4>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${badgeClass(progressLevel.variant)}`}>{progressLevel.level}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><FiBookOpen className="h-3.5 w-3.5" />{subject.completedLessons}/{subject.totalLessons} lessons</span>
                      <span className="inline-flex items-center gap-1"><FiClock className="h-3.5 w-3.5" />{formatTime(subject.timeSpent)}</span>
                      <span className="inline-flex items-center gap-1"><FiTarget className="h-3.5 w-3.5" />{formatPercentage(subject.averageScore)} avg</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-slate-600 rounded-full transition-all" style={{ width: `${subject.progress}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-10">{formatPercentage(subject.progress)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {progressData?.timeline && progressData.timeline.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
            <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Timeline</h3>
            <div className="space-y-4">
              {progressData.timeline.map((event, index) => (
                <div key={index} className="flex gap-3">
                  <div className="h-2 w-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-medium text-slate-900">{event.title}</h4>
                      <span className="text-xs text-slate-500">{formatDate(event.date)}</span>
                    </div>
                    {event.description && <p className="text-sm text-slate-600 mt-0.5">{event.description}</p>}
                    {event.score != null && (
                      <span className={`inline-flex mt-2 px-2 py-0.5 rounded text-xs font-medium ${event.score >= 80 ? 'bg-emerald-100 text-emerald-800' : event.score >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                        Score: {formatPercentage(event.score)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {progressData?.goals && progressData.goals.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
            <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Goals</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {progressData.goals.map((goal, index) => (
                <div key={index} className="p-4 rounded-lg border border-slate-100 bg-slate-50/50">
                  <div className="flex items-start gap-3">
                    <FiTarget className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-slate-900">{goal.title}</h4>
                      <span className="text-xs text-slate-500">Due: {formatDate(goal.deadline)}</span>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-slate-600 rounded-full" style={{ width: `${goal.progress}%` }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{formatPercentage(goal.progress)}</span>
                      </div>
                      {goal.description && <p className="text-sm text-slate-600 mt-2">{goal.description}</p>}
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><FiBookOpen className="h-3.5 w-3.5" />{goal.completedTasks}/{goal.totalTasks} tasks</span>
                        <span className="inline-flex items-center gap-1"><FiCalendar className="h-3.5 w-3.5" />{goal.daysRemaining} days left</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {progressData?.weeklyActivity && progressData.weeklyActivity.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Weekly activity</h3>
            <p className="text-xs text-slate-500 mb-3">Study time (minutes)</p>
            <div className="flex items-end gap-2 h-24">
              {progressData.weeklyActivity.map((day, index) => {
                const maxM = Math.max(1, ...(progressData.weeklyActivity || []).map(d => d.minutes));
                const pct = (day.minutes / maxM) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex-1 flex flex-col justify-end min-h-[40px]">
                      <div className="h-full w-full rounded-t bg-slate-200 overflow-hidden" style={{ minHeight: '4px' }}>
                        <div className="bg-slate-600 w-full rounded-t transition-all" style={{ height: `${pct}%`, minHeight: day.minutes ? '4px' : 0 }} />
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">{day.day}</span>
                    <span className="text-xs font-medium text-slate-700">{day.minutes}m</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Progress;